import { useState, type FormEvent, type ReactNode } from "react";
import { Eye, EyeOff, AlertCircle } from "../../atoms/Icon";
import { cn } from "../../utils";

export interface SplitLoginProps {
  /** 우측 폼 상단 제목 (예: "로그인") */
  title?: string;
  /** 제목 아래 보조 설명 */
  subtitle?: string;
  /** 우상단 뱃지 텍스트 (예: "관리자 전용"). 없으면 미노출 */
  badge?: string;
  /** 브랜드명 (좌측 페인 + 우측 로고). 기본 "SAFERS" */
  brandName?: string;
  /** 좌측 페인 상단 보조 텍스트 (예: "Admin Console") */
  brandTagline?: string;
  /** 좌측 페인 큰 메시지 */
  heroTitle?: ReactNode;
  /** 좌측 페인 본문 */
  heroDescription?: ReactNode;
  /** 좌측 하단 푸터 (예: "© Pluxity · SAFERS Admin v0.1") */
  footer?: ReactNode;
  /** 폼 하단 안내 문구 */
  helpText?: ReactNode;
  loading?: boolean;
  /** 외부에서 주입하는 에러 메시지 (인증 실패 등) */
  error?: string | null;
  onSubmit: (data: { username: string; password: string; remember: boolean }) => void;
}

/**
 * 분할 로그인 화면 — 좌측 브랜드 페인 + 우측 카드 폼.
 * UI 전용. 인증 흐름/권한 가드는 onSubmit 콜백을 받는 호출부(admin/app)가 담당한다.
 */
export function SplitLogin({
  title = "로그인",
  subtitle,
  badge,
  brandName = "SAFERS",
  brandTagline,
  heroTitle,
  heroDescription,
  footer,
  helpText,
  loading = false,
  error,
  onSubmit,
}: SplitLoginProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    onSubmit({ username: username.trim(), password, remember });
  }

  return (
    <div className="flex min-h-screen bg-[var(--surface-base)]">
      {/* 좌측 브랜드 페인 — lg 이상에서만 */}
      <aside
        className="relative hidden lg:flex lg:w-[44%] xl:w-[48%] flex-col justify-between overflow-hidden p-12 text-white"
        style={{ background: "linear-gradient(135deg, #0e1116 0%, #1a1f29 70%, #2a1c12 100%)" }}
      >
        <div className="pointer-events-none absolute -top-40 -left-40 h-[32.5rem] w-[32.5rem] rounded-full border border-white/10" />
        <div className="pointer-events-none absolute -top-20 -left-20 h-[22.5rem] w-[22.5rem] rounded-full border border-white/[0.06]" />
        <div className="pointer-events-none absolute -bottom-48 -right-32 h-[38.75rem] w-[38.75rem] rounded-full border border-[var(--brand-primary)]/20" />
        <div className="pointer-events-none absolute bottom-10 right-20 h-[11.25rem] w-[11.25rem] rounded-full bg-[var(--brand-primary)]/[0.08] blur-2xl" />

        <div className="relative z-10 flex items-center gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-[var(--brand-primary)] text-[0.9375rem] font-bold text-white">
            {brandName.slice(0, 1)}
          </span>
          <div className="leading-tight">
            <div className="text-[0.9375rem] font-semibold">{brandName}</div>
            {brandTagline && <div className="text-[0.6875rem] text-white/50">{brandTagline}</div>}
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          {heroTitle && <div className="text-[1.75rem] font-bold leading-[1.25]">{heroTitle}</div>}
          {heroDescription && (
            <div className="mt-4 text-[0.8125rem] leading-[1.7] text-white/55">
              {heroDescription}
            </div>
          )}
          {footer && (
            <div className="mt-8 text-[0.6875rem] tracking-wide text-white/30">{footer}</div>
          )}
        </div>
      </aside>

      {/* 우측 폼 */}
      <main className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="mb-7 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-[var(--brand-primary)] text-[0.9375rem] font-bold text-white">
                {brandName.slice(0, 1)}
              </span>
              <div className="leading-tight">
                <div className="text-[0.9375rem] font-semibold text-[var(--text-primary)]">
                  {brandName}
                </div>
                {brandTagline && (
                  <div className="text-[0.6875rem] text-[var(--text-tertiary)]">{brandTagline}</div>
                )}
              </div>
            </div>
            {badge && (
              <span className="rounded-full bg-[var(--warning-soft)] px-2 py-0.5 text-[0.6875rem] font-semibold text-[var(--warning)]">
                {badge}
              </span>
            )}
          </div>

          <div className="rounded-lg border border-[var(--border-default)] bg-[var(--surface-overlay)] p-7 shadow-[var(--shadow-sm)]">
            <div className="mb-5">
              <div className="text-[1.125rem] font-semibold text-[var(--text-primary)]">
                {title}
              </div>
              {subtitle && (
                <div className="mt-1 text-[0.75rem] text-[var(--text-tertiary)]">{subtitle}</div>
              )}
            </div>

            {error && (
              <div
                role="alert"
                className="mb-4 flex items-start gap-2 rounded-md border border-[var(--danger)]/30 bg-[var(--danger)]/[0.08] p-3 text-[0.75rem] text-[var(--danger)]"
              >
                <AlertCircle size="sm" className="mt-[0.0625rem] flex-none" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <Field label="아이디">
                <input
                  className={inputClass}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="아이디"
                  autoComplete="username"
                  autoFocus
                />
              </Field>

              <Field label="비밀번호">
                <div className="relative">
                  <input
                    className={cn(inputClass, "pr-9")}
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="비밀번호"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? "비밀번호 숨기기" : "비밀번호 보기"}
                    tabIndex={-1}
                  >
                    {showPassword ? <Eye size="sm" /> : <EyeOff size="sm" />}
                  </button>
                </div>
              </Field>

              <label className="flex cursor-pointer items-center gap-2 text-[0.75rem] text-[var(--text-secondary)]">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[var(--brand-primary)]"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                <span>로그인 정보 기억</span>
              </label>

              <button
                type="submit"
                className="mt-1 inline-flex h-9 items-center justify-center rounded-[var(--radius-sm)] bg-[var(--brand-primary)] px-4 text-[0.8125rem] font-semibold text-white transition-[filter] hover:brightness-95 disabled:opacity-60"
                disabled={loading}
              >
                {loading ? "로그인 중…" : "로그인"}
              </button>
            </form>
          </div>

          {helpText && (
            <div className="mt-5 text-center text-[0.6875rem] text-[var(--text-tertiary)]">
              {helpText}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

const inputClass =
  "h-9 w-full rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2.5 text-[0.8125rem] text-[var(--text-primary)] outline-none focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary)]/20";

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[0.75rem] font-medium text-[var(--text-tertiary)]">{label}</span>
      {children}
    </div>
  );
}
